import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import yardService from "services/yardService";
import { ACTION_BUTTON_SX, SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { READ_ONLY_MESSAGE, useShiftPermissions } from "shifts/permissions";
import { ShiftHubOption } from "shifts/models";
import { YardLookupsResponse, YardTemplateDay, YardTemplateShift } from "yard/models";

// Yard Schedule — the weekly template for a yard. Replaces the legacy Logistrics
// YardSchedule / YardShift / YardShiftAllocation trio, which split the same data over three
// pages.
//
// One shift is edited at a time. A busy yard carries hundreds of template rows, and rendering
// an allocation editor for every one at once puts tens of thousands of inputs on the page and
// locks the browser up. The list on the left is cheap; the editor on the right is not.
//
// BNSF-owned rows are read-only: the legacy pages let you edit and hard-delete them with no
// way to tell BNSF, which quietly desynchronised the two systems.
//
// Edit rights split in two here. A field manager allocates the week -- Driver, Vehicle,
// Client, Type -- but start and end times stay Admin-only: the App feeds those times and
// payroll bills off them, so moving one is not the same kind of change as swapping a driver.

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIME_PATTERN = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

const emptyLookups: YardLookupsResponse = {
  drivers: [],
  vehicles: [],
  clients: [],
  shiftTypes: [],
  driverChangeReasons: [],
  vehicleChangeReasons: [],
};

const buildEmptyShift = (shiftName: string): YardTemplateShift => ({
  shiftName,
  isBnsfOwned: false,
  days: Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    yardShiftId: null,
    timeStart: "",
    timeEnd: "",
    driverId: null,
    driverName: "",
    vehicleId: null,
    unitNumber: "",
    clientId: null,
    clientName: "",
    shiftTypeId: null,
    shiftType: "",
    isBnsfOwned: false,
  })),
});

const YardSchedule = () => {
  const { canEditYardSchedule, canEditYardScheduleTimes } = useShiftPermissions();
  // A brand new shift has no times, and every allocation on a day is disabled until that day
  // has a start time -- so creating one is only useful to someone who can set times.
  const canAddShift = canEditYardSchedule && canEditYardScheduleTimes;
  const canSave = canEditYardSchedule || canEditYardScheduleTimes;
  const [hubs, setHubs] = useState<ShiftHubOption[]>([]);
  const [hubId, setHubId] = useState<number | "">("");
  const [shifts, setShifts] = useState<YardTemplateShift[]>([]);
  const [lookups, setLookups] = useState<YardLookupsResponse>(emptyLookups);
  const [selectedName, setSelectedName] = useState<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [newShiftName, setNewShiftName] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    shiftManagementService
      .getHubs()
      .then(setHubs)
      .catch(() => toast.error("Could not load yards."));
  }, []);

  const load = useCallback(async () => {
    if (hubId === "") {
      toast.warn("Pick a yard first.");
      return;
    }

    setIsLoading(true);
    setSelectedName("");
    setIsDirty(false);
    try {
      const [template, lookupData] = await Promise.all([
        yardService.getTemplate(Number(hubId)),
        yardService.getLookups(Number(hubId)),
      ]);
      setShifts(template.shifts ?? []);
      setLookups(lookupData);
      setHasSearched(true);
    } catch {
      toast.error("Could not load the yard schedule.");
    } finally {
      setIsLoading(false);
    }
  }, [hubId]);

  const visibleShifts = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? shifts.filter((s) => s.shiftName.toLowerCase().includes(needle)) : shifts;
  }, [shifts, filter]);

  const selected = shifts.find((s) => s.shiftName === selectedName) ?? null;

  const selectShift = (shiftName: string) => {
    if (isDirty && !window.confirm("Discard unsaved changes to this shift?")) return;
    setSelectedName(shiftName);
    setIsDirty(false);
  };

  const updateDay = (dayOfWeek: number, changes: Partial<YardTemplateDay>) => {
    setShifts((current) =>
      current.map((shift) =>
        shift.shiftName !== selectedName
          ? shift
          : { ...shift, days: shift.days.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...changes } : d)) }
      )
    );
    setIsDirty(true);
  };

  const save = async () => {
    if (!selected) return;

    for (const day of selected.days) {
      const hasStart = !!day.timeStart;
      const hasEnd = !!day.timeEnd;
      if (hasStart !== hasEnd) {
        toast.error(`${DAY_NAMES[day.dayOfWeek]} needs both a start and an end time.`);
        return;
      }
      if (hasStart && (!TIME_PATTERN.test(day.timeStart) || !TIME_PATTERN.test(day.timeEnd))) {
        toast.error(`${DAY_NAMES[day.dayOfWeek]} times must be HH:mm.`);
        return;
      }
      if (!day.clientId && (day.driverId || day.vehicleId)) {
        toast.error(`${DAY_NAMES[day.dayOfWeek]} has a driver or vehicle, so it needs a client.`);
        return;
      }
    }

    setIsSaving(true);
    try {
      const result = await yardService.saveTemplate({
        hubId: Number(hubId),
        shiftName: selected.shiftName,
        days: selected.days,
      });
      if (result.success) {
        toast.success(result.message || "Saved.");
        const keep = selected.shiftName;
        await load();
        setSelectedName(keep);
      } else {
        toast.error(result.message || "Could not save the shift.");
      }
    } catch {
      toast.error("Could not save the shift.");
    } finally {
      setIsSaving(false);
    }
  };

  const addShift = () => {
    const name = newShiftName.trim();
    if (!name) return;
    if (shifts.some((s) => s.shiftName.toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" already exists at this yard.`);
      return;
    }

    setShifts((current) => [...current, buildEmptyShift(name)]);
    setSelectedName(name);
    setNewShiftName("");
    setIsDirty(true);
  };

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Yard Schedule
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        The weekly template for a yard. Shifts imported into a week's daily schedule start from these times and
        allocations.
        {!canSave && ` ${READ_ONLY_MESSAGE}`}
        {canEditYardSchedule && !canEditYardScheduleTimes &&
          " Start and end times are maintained by administrators, so they are shown here but cannot be changed."}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="yard-hub-label">Yard</InputLabel>
            <Select
              labelId="yard-hub-label"
              label="Yard"
              value={hubId}
              onChange={(e) => setHubId(e.target.value as number)}
            >
              {hubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  {hub.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" color="info" onClick={load} disabled={isLoading}>
            {isLoading ? "Loading…" : "Get Schedule"}
          </Button>

          {hasSearched && canAddShift && (
            <>
              <Box sx={{ flex: 1 }} />
              <TextField
                label="New shift name"
                size="small"
                value={newShiftName}
                onChange={(e) => setNewShiftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addShift();
                }}
                sx={{ width: 220 }}
              />
              <Button variant="contained" sx={ACTION_BUTTON_SX} onClick={addShift} disabled={!newShiftName.trim()}>
                Add Shift
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {!hasSearched && <Typography sx={{ color: "text.secondary" }}>Pick a yard, then Get Schedule.</Typography>}

      {hasSearched && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
          <Paper sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
            <Box sx={{ p: 1.5 }}>
              <TextField
                placeholder="Filter shifts"
                size="small"
                fullWidth
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                {visibleShifts.length} of {shifts.length} shift{shifts.length === 1 ? "" : "s"}
              </Typography>
            </Box>
            <Divider />
            <List dense sx={{ maxHeight: "calc(100vh - 380px)", minHeight: 260, overflow: "auto", py: 0 }}>
              {visibleShifts.map((shift) => (
                <ListItemButton
                  key={shift.shiftName}
                  selected={shift.shiftName === selectedName}
                  onClick={() => selectShift(shift.shiftName)}
                >
                  <ListItemText
                    primary={shift.shiftName}
                    secondary={summarize(shift)}
                    primaryTypographyProps={{ fontSize: ".85em" }}
                    secondaryTypographyProps={{ fontSize: ".75em" }}
                  />
                  {shift.isBnsfOwned && (
                    <Tooltip title="Managed by BNSF">
                      <LockIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                </ListItemButton>
              ))}
              {visibleShifts.length === 0 && (
                <Box sx={{ p: 2, color: "text.secondary", fontSize: ".85em" }}>No shifts match that filter.</Box>
              )}
            </List>
          </Paper>

          <Paper sx={{ flex: 1, p: 2, minWidth: 0 }}>
            {!selected && (
              <Typography sx={{ color: "text.secondary" }}>Pick a shift on the left to edit its week.</Typography>
            )}

            {selected && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1">{selected.shiftName}</Typography>
                  {selected.isBnsfOwned && (
                    <Tooltip title="This shift comes from BNSF. Changes have to be made on their side.">
                      <Chip
                        icon={<LockIcon />}
                        label="Managed by BNSF"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {!selected.isBnsfOwned && canSave && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={SAVE_BUTTON_SX}
                      disabled={isSaving || !isDirty}
                      onClick={save}
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </Button>
                  )}
                </Stack>

                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                  Clearing both times removes that day from the schedule. An end time earlier than the start means the
                  shift runs past midnight.
                </Typography>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 780 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 80 }} />
                        {DAY_NAMES.map((day) => (
                          <TableCell key={day} align="center">
                            {day}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TemplateRow
                        label="Start"
                        shift={selected}
                        render={(day) => (
                          <TextField
                            value={day.timeStart}
                            onChange={(e) => updateDay(day.dayOfWeek, { timeStart: e.target.value })}
                            disabled={selected.isBnsfOwned || !canEditYardScheduleTimes}
                            placeholder="HH:mm"
                            size="small"
                            variant="standard"
                            inputProps={{ style: { fontSize: ".85em", textAlign: "center" } }}
                            sx={{ width: 68 }}
                          />
                        )}
                      />
                      <TemplateRow
                        label="End"
                        shift={selected}
                        render={(day) => (
                          <TextField
                            value={day.timeEnd}
                            onChange={(e) => updateDay(day.dayOfWeek, { timeEnd: e.target.value })}
                            disabled={selected.isBnsfOwned || !canEditYardScheduleTimes}
                            placeholder="HH:mm"
                            size="small"
                            variant="standard"
                            inputProps={{ style: { fontSize: ".85em", textAlign: "center" } }}
                            sx={{ width: 68 }}
                          />
                        )}
                      />
                      <TemplateRow
                        label="Driver"
                        shift={selected}
                        render={(day) => (
                          <AllocationSelect
                            value={day.driverId}
                            options={lookups.drivers}
                            disabled={selected.isBnsfOwned || !day.timeStart || !canEditYardSchedule}
                            onChange={(v) => updateDay(day.dayOfWeek, { driverId: v })}
                          />
                        )}
                      />
                      <TemplateRow
                        label="Vehicle"
                        shift={selected}
                        render={(day) => (
                          <AllocationSelect
                            value={day.vehicleId}
                            options={lookups.vehicles}
                            disabled={selected.isBnsfOwned || !day.timeStart || !canEditYardSchedule}
                            onChange={(v) => updateDay(day.dayOfWeek, { vehicleId: v })}
                          />
                        )}
                      />
                      <TemplateRow
                        label="Client"
                        shift={selected}
                        render={(day) => (
                          <AllocationSelect
                            value={day.clientId}
                            options={lookups.clients}
                            disabled={selected.isBnsfOwned || !day.timeStart || !canEditYardSchedule}
                            onChange={(v) => updateDay(day.dayOfWeek, { clientId: v })}
                          />
                        )}
                      />
                      <TemplateRow
                        label="Type"
                        shift={selected}
                        render={(day) => (
                          <AllocationSelect
                            value={day.shiftTypeId}
                            options={lookups.shiftTypes}
                            disabled={selected.isBnsfOwned || !day.timeStart || !canEditYardSchedule}
                            onChange={(v) => updateDay(day.dayOfWeek, { shiftTypeId: v })}
                          />
                        )}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

/// A one-line "which days are scheduled" hint for the picker list.
const summarize = (shift: YardTemplateShift): string => {
  const scheduled = shift.days.filter((d) => d.timeStart);
  if (scheduled.length === 0) return "not scheduled";
  const days = scheduled.map((d) => DAY_NAMES[d.dayOfWeek]).join(" ");
  const first = scheduled[0];
  return `${days} · ${first.timeStart}–${first.timeEnd}`;
};

const TemplateRow = ({
  label,
  shift,
  render,
}: {
  label: string;
  shift: YardTemplateShift;
  render: (day: YardTemplateDay) => JSX.Element;
}) => (
  <TableRow>
    <TableCell sx={{ color: "text.secondary", fontSize: ".8em" }}>{label}</TableCell>
    {shift.days.map((day) => (
      <TableCell key={day.dayOfWeek} align="center">
        {render(day)}
      </TableCell>
    ))}
  </TableRow>
);

const AllocationSelect = ({
  value,
  options,
  disabled,
  onChange,
}: {
  value: number | null;
  options: { id: number; name: string }[];
  disabled: boolean;
  onChange: (value: number | null) => void;
}) => (
  <Select
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value) || null)}
    disabled={disabled}
    variant="standard"
    sx={{ width: "100%", minWidth: 84, fontSize: ".8em" }}
  >
    <MenuItem value={0}>—</MenuItem>
    {options.map((option) => (
      <MenuItem key={option.id} value={option.id} sx={{ fontSize: ".85em" }}>
        {option.name}
      </MenuItem>
    ))}
  </Select>
);

export default YardSchedule;
